import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../api";
import { useAuth } from "../contexts/AuthContext";
import { useNotificationContext } from "../contexts/NotificationContext";
import { devLog, logError } from "../utils/logger";

/**
 * Hook for managing user notifications
 * Follows the same architecture as useMyNotes and useNearbyNotes
 */
export function useNotifications(initialFilter = "all", limit = 20) {
  // Get current user from auth context
  const { user } = useAuth();

  // Get notification context to update sidebar badge
  const { refetchUnreadCount } = useNotificationContext();

  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({
    all: 0,
    likes: 0,
    comments: 0,
    system: 0,
  });
  const [lastSeenAt, setLastSeenAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(initialFilter);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);

  devLog("useNotifications hook:", { filter, api, userId: user?.id });

  if (!api) {
    logError("api is not defined!");
    throw new Error("api is not available");
  }

  // Fetch last seen timestamp
  const fetchLastSeen = useCallback(async () => {
    if (!user?.id) return;

    try {
      const response = await api.getLastSeen();
      setLastSeenAt(response.data.lastSeenAt);
    } catch (err) {
      logError("Failed to fetch last seen:", err);
      // Default to 2 hours ago
      setLastSeenAt(Date.now() - 2 * 60 * 60 * 1000);
    }
  }, [user]);

  // Fetch notifications based on current filter
  const fetchNotifications = useCallback(async () => {
    if (!user?.id) {
      devLog("useNotifications: No user, skipping fetch");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setOffset(0);

      const response = await api.getNotifications({
        filter,
        limit,
        offset: 0,
      });

      setNotifications(response.data);
      setHasMore(response.hasMore);
    } catch (err) {
      setError(err.message || "Failed to load notifications");
      logError("Failed to fetch notifications:", err);
    } finally {
      setLoading(false);
    }
  }, [user, filter, limit]);

  // Fetch stats separately (lighter weight, can be cached)
  const fetchStats = useCallback(async () => {
    if (!user?.id) return;

    try {
      setStatsLoading(true);
      const response = await api.getNotificationStats();
      setStats(response.data);
    } catch (err) {
      logError("Failed to fetch notification stats:", err);
    } finally {
      setStatsLoading(false);
    }
  }, [user]);

  // Fetch last seen and stats once on mount
  useEffect(() => {
    fetchLastSeen();
    fetchStats();
  }, [fetchLastSeen, fetchStats]);

  // Fetch notifications when filter changes
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Load more notifications (pagination)
  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || !user?.id) return;

    try {
      setLoadingMore(true);
      const newOffset = offset + limit;

      const response = await api.getNotifications({
        filter,
        limit,
        offset: newOffset,
      });

      setNotifications((prev) => [...prev, ...response.data]);
      setHasMore(response.hasMore);
      setOffset(newOffset);
    } catch (err) {
      setError(err.message || "Failed to load more notifications");
      logError("Failed to load more notifications:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, offset, filter, limit, user]);

  // Mark a single notification as read
  const markAsRead = useCallback(
    async (notificationId) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }

      try {
        await api.markNotificationRead(notificationId);

        // Update local state
        setNotifications((prev) =>
          prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)),
        );

        // Refresh stats for this page
        await fetchStats();

        // Update sidebar badge immediately
        await refetchUnreadCount();
      } catch (err) {
        setError(err.message);
        logError("Failed to mark notification as read:", err);
        throw err;
      }
    },
    [user, fetchStats, refetchUnreadCount],
  );

  // Mark all visible notifications as read (respects current filter)
  const markAllAsRead = useCallback(async () => {
    if (!user?.id) {
      throw new Error("User not authenticated");
    }

    try {
      await api.markAllNotificationsRead(filter);

      // Update local state
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));

      // Refresh stats for this page
      await fetchStats();

      // Update sidebar badge immediately
      await refetchUnreadCount();
    } catch (err) {
      setError(err.message);
      logError("Failed to mark all as read:", err);
      throw err;
    }
  }, [user, filter, fetchStats, refetchUnreadCount]);

  // Update last seen timestamp
  const updateLastSeen = useCallback(
    async (timestamp = Date.now()) => {
      if (!user?.id) return;

      try {
        await api.updateLastSeen(timestamp);
        setLastSeenAt(timestamp);
      } catch (err) {
        logError("Failed to update last seen:", err);
      }
    },
    [user],
  );

  // Partition notifications into new and older based on lastSeenAt
  const { newNotifications, olderNotifications } = useMemo(() => {
    if (!lastSeenAt) {
      return { newNotifications: [], olderNotifications: notifications };
    }

    const newer = [];
    const older = [];

    for (const n of notifications) {
      if (n.createdAt > lastSeenAt) {
        newer.push(n);
      } else {
        older.push(n);
      }
    }

    return { newNotifications: newer, olderNotifications: older };
  }, [notifications, lastSeenAt]);

  return {
    notifications,
    newNotifications,
    olderNotifications,
    stats,
    statsLoading,
    loading,
    loadingMore,
    error,
    filter,
    setFilter,
    hasMore,
    lastSeenAt,
    loadMore,
    markAsRead,
    markAllAsRead,
    updateLastSeen,
    refetch: fetchNotifications,
    refetchStats: fetchStats,
  };
}
